import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { uploadImagesMutation } from '../apollo/image-uploads';
import LicenseField from '../components/forms/license-field';
import DropzoneFileField from '../components/forms/dropzone-file-field';
import { useAppContext } from '../contexts/app';
import RadioGroupField from './forms/radio-group-field';
import { meQuery } from '../apollo/auth';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    backgroundColor: theme.palette.secondary.background,
  },
  dropzone: {
    marginBottom: theme.spacing(2),
  },
  dropzoneArea: {
    backgroundColor: theme.palette.secondary.background,
  },
  license: {
    marginBottom: theme.spacing(1),
  },
  anonymous: {
    marginBottom: theme.spacing(1),
  },
  hostGroup: {
    marginLeft: theme.spacing(0.5),
  },
  buttons: {
    marginTop: theme.spacing(1),
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
}));

export const formSchema = Yup.object().shape({
  images: Yup.array().of(
    Yup.mixed(),
  ).required('At least one image is required'),
  license: Yup.string().nullable().required('License is required'),
  anonymous: Yup.bool().required('License is required'),
});

const IMAGES_BROKEN = true;

const UploadImagesBox = ({ className, group, event, onClose }) => {
  const classes = useStyles();
  const { loggedInUser } = useAppContext();

  const [uploadImages] = useMutation(uploadImagesMutation);

  const handleSubmit = useCallback((values) => {
    uploadImages({
      variables: {
        data: {
          ..._pick(values, [
            'images',
            'license',
            'anonymous',
          ]),
          eventId: _get(event, 'id'),
          groupId: _get(group, 'id'),
        },
      },
      update: (proxy) => {
        // update default license on user
        const meData = proxy.readQuery({
          query: meQuery,
        });
        proxy.writeQuery({
          query: meQuery,
          data: {
            ...meData,
            me: {
              ...meData.me,
              defaultLicense: values.license,
            },
          },
        });
      },
    }).then(() => {
      location.reload();
    });
  }, [event, group]);

  const initialValues = useMemo(() => ({
    images: [],
    license: _get(loggedInUser, 'defaultLicense') || 'by',
    anonymous: false,
  }), [loggedInUser]);

  if (!event && !group) {
    return <p>Must be attached to an event or a group.</p>;
  }

  return (
    <Paper className={clsx(classes.paper, className)} elevation={2}>
      {IMAGES_BROKEN && (
        <Alert severity="info" className={classes.alert}>
          Image uploads are temporarily disabled.
        </Alert>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {({ submitCount, isSubmitting }) => {
          const disabled = isSubmitting || IMAGES_BROKEN;
          const forceTouched = submitCount > 0;
          return (
            <Form>
              <DropzoneFileField
                name="images"
                disabled={disabled}
                touched={forceTouched}
                acceptedFiles={['image/*']}
                dropzoneText="Drag and drop images or click here"
                className={classes.dropzone}
                dropzoneClass={classes.dropzoneArea}
                filesLimit={10}
                label="Images (max 10 at a time)"
              />

              <LicenseField
                name="license"
                label="License"
                disabled={disabled}
                touched={forceTouched}
                className={classes.license}
              />

              <RadioGroupField
                name="anonymous"
                label="Attribution"
                options={[
                  { value: false, label: `Attribute to me (${_get(loggedInUser, 'displayName')})` },
                  { value: true, label: 'Share anonymously' },
                ]}
                disabled={disabled}
                touched={forceTouched}
                className={classes.anonymous}
              />

              <div className={classes.buttons}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                >
                  {isSubmitting ? 'Uploading...' : 'Upload'}
                </Button>
                {onClose && (
                  <Button
                    variant="contained"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
};

UploadImagesBox.propTypes = {
  group: PropTypes.object,
  event: PropTypes.object,
  onClose: PropTypes.func,
  className: PropTypes.string,
};

export default UploadImagesBox;
